/**
 * VirtualAccountsSection — User VA List
 * 
 * PRD-USER-VA: Virtual Accounts Banking Experience
 * 
 * Displays user's virtual accounts with:
 * - Currency-based grouping (USD, EUR, MXN)
 * - Bank details (masked for security)
 * - Quick copy functionality
 * - Link to full details page
 */

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { SFIcon } from '@/components/ui/sf-icon';
import { Text } from '@/components/ui/typography';
import { useVirtualAccounts, VirtualAccountInstruction } from '@/hooks/useVirtualAccounts';
import { toast } from '@/hooks/use-toast';
import { sfIcons } from '@/lib/sf-icons';
import { cn } from '@/lib/utils';
import { semanticTokens } from '@packages/config/theme';
import { EmptyState } from '@supabank/ui';
import { Building2, ChevronRight, Copy, CreditCard, Plus } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface VirtualAccountCardProps {
  account: VirtualAccountInstruction;
  onViewDetails: () => void;
}

function VirtualAccountCard({ account, onViewDetails }: VirtualAccountCardProps) {
  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied`);
  };

  // Get display info based on currency
  const getCurrencyInfo = () => {
    switch (account.currency?.toUpperCase()) {
      case 'USD':
        return {
          icon: '🇺🇸',
          label: 'US Dollar',
          primaryField: account.accountNumber,
          primaryLabel: 'Account',
          secondaryField: account.routingNumber,
          secondaryLabel: 'Routing',
        };
      case 'EUR':
        return {
          icon: '🇪🇺',
          label: 'Euro',
          primaryField: account.iban,
          primaryLabel: 'IBAN',
          secondaryField: account.bic,
          secondaryLabel: 'BIC',
        };
      case 'MXN':
        return {
          icon: '🇲🇽',
          label: 'Mexican Peso',
          primaryField: account.clabe,
          primaryLabel: 'CLABE',
          secondaryField: null,
          secondaryLabel: null,
        };
      default:
        return {
          icon: '💰',
          label: account.currency || 'Unknown',
          primaryField: account.accountNumber || account.iban,
          primaryLabel: 'Account',
          secondaryField: null,
          secondaryLabel: null,
        };
    }
  };

  const info = getCurrencyInfo();
  const statusColor = account.status === 'activated' 
    ? 'bg-green-500/10 text-green-600' 
    : account.status === 'deactivated' 
      ? 'bg-red-500/10 text-red-600'
      : 'bg-yellow-500/10 text-yellow-600';

  return (
    <Card 
      className={cn(
        "cursor-pointer transition-all hover:shadow-md",
        semanticTokens.motion.standard
      )}
      onClick={onViewDetails}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between gap-4">
          {/* Left: Currency & Details */}
          <div className="flex items-start gap-3 flex-1 min-w-0">
            <div className={cn(
              "w-10 h-10 rounded-full flex items-center justify-center text-lg",
              semanticTokens.bg.muted
            )}>
              {info.icon}
            </div>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <Text className="font-medium">{info.label}</Text>
                <Badge variant="outline" className={cn("text-xs", statusColor)}>
                  {account.status || 'pending'}
                </Badge>
              </div>
              
              {/* Bank name */}
              {account.bankName && (
                <Text className={cn("text-sm", semanticTokens.fg.muted)}>
                  {account.bankName}
                </Text>
              )}
              
              {/* Primary field (Account/IBAN/CLABE) */}
              {info.primaryField && (
                <div className="flex items-center gap-2 mt-2">
                  <code className={cn(
                    "text-xs px-2 py-1 rounded font-mono",
                    semanticTokens.bg.muted
                  )}>
                    {info.primaryLabel}: {maskField(info.primaryField)}
                  </code>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-6 w-6"
                    onClick={(e) => {
                      e.stopPropagation();
                      copyToClipboard(info.primaryField!, info.primaryLabel);
                    }}
                  >
                    <Copy className="h-3 w-3" />
                  </Button>
                </div>
              )}
            </div>
          </div>
          
          {/* Right: Arrow */}
          <ChevronRight className={cn("h-5 w-5 shrink-0", semanticTokens.fg.muted)} />
        </div>
      </CardContent>
    </Card>
  );
}

// Mask sensitive fields (show last 4 chars)
function maskField(value: string): string {
  if (!value || value.length <= 4) return value;
  const visible = value.slice(-4);
  const masked = '•'.repeat(Math.min(value.length - 4, 8));
  return `${masked}${visible}`;
}

// Skeleton for loading state
function VirtualAccountsSkeleton() {
  return (
    <div className="space-y-3">
      {[1, 2].map((i) => (
        <Card key={i}>
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Skeleton className="w-10 h-10 rounded-full" />
              <div className="flex-1 space-y-2">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-3 w-48" />
                <Skeleton className="h-6 w-40" />
              </div>
            </div>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

interface VirtualAccountsSectionProps {
  onCreateVA?: () => void;
}

export function VirtualAccountsSection({ onCreateVA }: VirtualAccountsSectionProps) {
  const navigate = useNavigate();
  const { data, isLoading, error } = useVirtualAccounts(true);

  const accounts = data?.accounts || [];
  const hasAccounts = accounts.length > 0;

  // Group by currency
  const groupedAccounts = accounts.reduce((groups, account) => {
    const currency = account.currency?.toUpperCase() || 'OTHER';
    if (!groups[currency]) groups[currency] = [];
    groups[currency].push(account);
    return groups;
  }, {} as Record<string, VirtualAccountInstruction[]>);

  const handleViewDetails = (accountId: string) => {
    navigate(`/dashboard/virtual-accounts/${accountId}`);
  };

  if (isLoading) {
    return (
      <section className="mt-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <Building2 className={cn("h-5 w-5", semanticTokens.fg.muted)} />
            <Text className="font-semibold">Virtual Accounts</Text>
          </div>
        </div>
        <VirtualAccountsSkeleton />
      </section>
    );
  }

  if (error) {
    return (
      <section className="mt-6">
        <Card className={cn(semanticTokens.danger.bg, semanticTokens.danger.border)}>
          <CardContent className="p-4">
            <Text className={semanticTokens.danger.fg}>
              Failed to load virtual accounts. Please try again.
            </Text>
          </CardContent>
        </Card>
      </section>
    );
  }

  return (
    <section className="mt-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Building2 className={cn("h-5 w-5", semanticTokens.fg.muted)} />
          <Text className="font-semibold">Virtual Accounts</Text>
          {hasAccounts && (
            <Badge variant="secondary" className="text-xs">
              {accounts.length}
            </Badge>
          )}
        </div>
        
        {onCreateVA && (
          <Button
            variant="outline"
            size="sm"
            onClick={onCreateVA}
            className="gap-1"
          >
            <Plus className="h-4 w-4" />
            Add
          </Button>
        )}
      </div>

      {/* Content */}
      {!hasAccounts ? (
        <Card>
          <CardContent className="py-8">
            <EmptyState
              icon={CreditCard}
              title="No virtual accounts yet"
              description="Virtual accounts let you receive bank transfers in USD, EUR, or MXN that automatically convert to crypto."
              action={onCreateVA ? {
                label: "Create Virtual Account",
                onClick: onCreateVA
              } : undefined}
            />
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {Object.entries(groupedAccounts).map(([currency, currencyAccounts]) => (
            <div key={currency} className="space-y-2">
              {/* Currency group header (only if multiple currencies) */}
              {Object.keys(groupedAccounts).length > 1 && (
                <Text className={cn("text-sm font-medium", semanticTokens.fg.muted)}>
                  {currency}
                </Text>
              )}
              
              {/* Accounts */}
              {currencyAccounts.map((account) => (
                <VirtualAccountCard
                  key={account.id}
                  account={account}
                  onViewDetails={() => handleViewDetails(account.id)}
                />
              ))}
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

export default VirtualAccountsSection;
